/**
 * Whether this bundle is running inside the desktop launcher.
 *
 * One deployment serves two things now. `apps/launcher` used to have a frontend
 * of its own — sixty lines of hand-written HTML that called
 * `window.__TAURI__.core.invoke` directly — and it was replaced by this app
 * rather than grown, because a launcher and a player's library are the same
 * product with two extra screens, and maintaining two React trees over one API
 * is how they drift.
 *
 * So the same files are served by Cloudflare to a browser and by Tauri's asset
 * protocol to a webview, and exactly one question separates them. This is that
 * question, asked in one place so that every screen gets the same answer — the
 * pattern `origins.ts` uses for "where is the storefront", which is the closest
 * existing thing.
 *
 * ## Asked of the bridge, never of the user agent
 *
 * A user-agent string is the obvious way and the wrong one. WebView2 and
 * WKWebView both send something close enough to Chrome and Safari that any test
 * you write is either a substring that a browser update makes true, or one that
 * a webview update makes false — and both failures are silent. The launcher
 * screens are useless without the IPC bridge anyway, so the honest question is
 * not "does this look like a desktop app" but "can I call the Rust side", and
 * that is a function reference which either exists or does not.
 *
 * ## `__TAURI__` rather than `__TAURI_INTERNALS__`
 *
 * Tauri v2 always injects `__TAURI_INTERNALS__`, and it only injects
 * `__TAURI__` when `withGlobalTauri` is on. `__TAURI_INTERNALS__` is therefore
 * the more truthful answer to "is this a Tauri webview" — and it is the wrong
 * one to gate on, because what these screens need is a bridge they can *call*.
 * A launcher built with `withGlobalTauri` off would answer yes to the truthful
 * question and then throw on every button. Gating on the thing we actually call
 * means that build degrades to looking like an ordinary browser, which is a
 * working app rather than a broken one.
 *
 * See `apps/launcher/README.md` for why `withGlobalTauri` is on and stays on.
 */

/**
 * The one global this app touches, typed as narrowly as it is used.
 *
 * Deliberately not `@tauri-apps/api`. A bundler is in play now, so that package
 * *could* be imported properly — but it would then be a dependency of a web app
 * that Cloudflare serves to browsers, and the first time somebody imports it at
 * module scope for a type, a desktop SDK ships to every player who never
 * installed anything. The surface is one function; describing it here costs
 * four lines and keeps the launcher's Tauri version pinned in one place, the
 * Rust crate, rather than in two that can disagree.
 */
export type Invoke = (command: string, args?: Record<string, unknown>) => Promise<unknown>;

/**
 * Subscribing to what the Rust side says without being asked.
 *
 * The second half of the bridge, and it exists because the download loop moved
 * into Rust. A 40GB build is about a hundred and sixty thousand chunks; when
 * this side drove the loop that was a hundred and sixty thousand `invoke`
 * calls, and the webview bridge became the bottleneck for the thing it was
 * supposed to be reporting on. Now one call starts the download and progress
 * arrives here.
 *
 * Typed as narrowly as it is used, for the same reason `Invoke` is: importing
 * `@tauri-apps/api` would ship a desktop SDK to every browser that loads the
 * store.
 */
export type Unlisten = () => void;

export type Listen = <T>(
  event: string,
  handler: (event: { payload: T }) => void,
) => Promise<Unlisten>;

interface TauriGlobal {
  core?: { invoke?: Invoke };
  event?: { listen?: Listen };
}

/**
 * Read once, at module load, and that is safe rather than lucky.
 *
 * Tauri injects the global from an initialisation script that runs before any
 * page script, so by the time this module is evaluated the answer cannot still
 * be changing. Recomputing it per call would suggest otherwise, and every
 * caller would have to wonder whether the answer could differ between two
 * renders of the same screen.
 */
const BRIDGE: Invoke | null = readBridge();
const EVENTS: Listen | null = readEvents();

function tauriGlobal(): TauriGlobal | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
}

function readBridge(): Invoke | null {
  const invoke = tauriGlobal()?.core?.invoke;
  return typeof invoke === 'function' ? invoke : null;
}

function readEvents(): Listen | null {
  const listen = tauriGlobal()?.event?.listen;
  return typeof listen === 'function' ? listen : null;
}

/** Whether the extra desktop surfaces should exist at all. */
export function isLauncher(): boolean {
  return BRIDGE !== null;
}

/**
 * The bridge, for `launcher-ipc.ts` and nothing else.
 *
 * Exported from here rather than reached for again over there so that
 * `window.__TAURI__` appears exactly once in this codebase. Null in a browser,
 * which is what makes the shared bundle safe to serve to one: nothing here
 * touches the global at import time beyond reading it, and no module-scope code
 * anywhere assumes it is there.
 */
export function launcherBridge(): Invoke | null {
  return BRIDGE;
}

/**
 * The event bridge, for `launcher-ipc.ts` and nothing else.
 *
 * Null in a browser, and also null in a launcher built with `withGlobalTauri`
 * off — the same degradation `launcherBridge` chooses, and for the same reason:
 * a screen that cannot subscribe should behave like one that has nothing to
 * subscribe to rather than throwing on mount.
 */
export function launcherEvents(): Listen | null {
  return EVENTS;
}
